class View360Tabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.#tabs.length || !this.#panels.length) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    const selectedTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.#tabs[0];
    if (selectedTab) this.select(selectedTab, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = true) {
    const controls = tab.getAttribute('aria-controls');

    for (const tabItem of this.#tabs) {
      const isActive = tabItem === tab;
      tabItem.setAttribute('aria-selected', String(isActive));
      tabItem.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.#panels) {
      const isActive = panel.id === controls;
      panel.hidden = !isActive;

      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }

    if (focus) tab.focus();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;

    this.select(tab, false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const activeTab = event.target.closest('[role="tab"]');
    if (!(activeTab instanceof HTMLButtonElement) || !this.contains(activeTab)) return;

    const currentIndex = this.#tabs.indexOf(activeTab);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = this.#tabs[nextIndex];
    if (!nextTab) return;

    this.select(nextTab);
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const panel = event.target.closest('[role="tabpanel"]');
    if (!(panel instanceof HTMLElement) || !this.contains(panel)) return;

    const tabId = panel.getAttribute('aria-labelledby');
    if (!tabId) return;

    const matchingTab = this.#tabs.find((tab) => tab.id === tabId);
    if (!matchingTab) return;

    this.select(matchingTab, false);
    matchingTab.scrollIntoView({ block: 'nearest', inline: 'center' });
  };
}

if (!customElements.get('view-360-tabs')) {
  customElements.define('view-360-tabs', View360Tabs);
}
